import React, { useEffect, useMemo, useState } from "react";
import { Mail, Phone, MapPin, Clock, ExternalLink } from "lucide-react";
import { apiClient } from "../../services/apiClientService";
import Seo from "../common/Seo";
import { useVendorStore } from "../../context/VendorStoreContext";

/** Same map URL rules as ShippingInfo pickup card — stays in sync with superuser storefront vendor. */
function storefrontMapUrls(vendor) {
  if (!vendor) {
    return { embed: "", open: "" };
  }
  const pickupPointLocation = String(vendor.pickupPointLocation || "").trim();
  const pickupPointGoogleMapsUrl = String(vendor.pickupPointGoogleMapsUrl || "").trim();
  const pickupPointLat = vendor.pickupPointLat;
  const pickupPointLng = vendor.pickupPointLng;

  let embed = "";
  if (
    typeof pickupPointLat === "number" &&
    typeof pickupPointLng === "number"
  ) {
    embed = `https://www.google.com/maps?q=${encodeURIComponent(
      `${pickupPointLat},${pickupPointLng}`,
    )}&output=embed`;
  }
  if (!embed && pickupPointGoogleMapsUrl) {
    try {
      const u = new URL(pickupPointGoogleMapsUrl);
      if (
        u.hostname.toLowerCase().includes("google.") &&
        u.pathname.includes("/maps")
      ) {
        if (!u.searchParams.get("output")) u.searchParams.set("output", "embed");
        embed = u.toString();
      }
    } catch {
      /* ignore */
    }
  }
  if (!embed && pickupPointLocation) {
    embed = `https://www.google.com/maps?q=${encodeURIComponent(
      pickupPointLocation,
    )}&output=embed`;
  }

  let open = pickupPointGoogleMapsUrl;
  if (
    !open &&
    typeof pickupPointLat === "number" &&
    typeof pickupPointLng === "number"
  ) {
    open = `https://www.google.com/maps?q=${encodeURIComponent(
      `${pickupPointLat},${pickupPointLng}`,
    )}`;
  }
  if (!open && pickupPointLocation) {
    open = `https://www.google.com/maps?q=${encodeURIComponent(pickupPointLocation)}`;
  }

  return { embed, open };
}

function splitAddressLines(text) {
  const t = String(text || "").trim();
  if (!t) return [];
  const byNl = t.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (byNl.length > 1) return byNl;
  const byComma = t.split(",").map((s) => s.trim()).filter(Boolean);
  return byComma.length ? byComma : [t];
}

/** Shown when browsing a vendor's slug URL (/:storeSlug/contact) */
function VendorContact({ vendor, storeSlug }) {
  const name = vendor.storeName?.trim() || vendor.name?.trim() || "Store";
  const vendorEmail = vendor.email?.trim() || "";
  const vendorPhone = vendor.phone?.trim() || "";
  const location = vendor.stateInfo?.trim() || vendor.storeAddress?.trim() || "";
  const { embed: mapEmbedSrc, open: mapOpenUrl } = storefrontMapUrls(vendor);
  const addressLines = splitAddressLines(vendor.pickupPointLocation);
  const hasMapOpen = !!mapOpenUrl;
  const hasMapEmbed = !!mapEmbedSrc;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/80 via-amber-50/25 to-stone-100 px-4 py-10 sm:py-14 text-stone-900">
      <Seo
        title={`Contact ${name}`}
        description={`Reach out to ${name} for order help, delivery, or pickup questions.`}
        canonical={`/${storeSlug}/contact`}
      />
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-stone-500">Support</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
              Contact {name}
            </h1>
            {location && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-stone-500">
                <MapPin className="h-4 w-4 shrink-0 text-orange-400" /> {location}
              </p>
            )}
            <p className="mt-3 max-w-xl text-sm leading-6 text-stone-600 sm:text-base">
              Need help with an order, delivery, pickup, or a product question? Use the details
              below—we typically respond quickly during business hours.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-stone-200/80 bg-white/80 p-4 shadow-sm ring-1 ring-black/5 sm:p-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70">
                  <Clock className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-stone-900">Business hours</p>
                  <p className="mt-0.5 text-sm text-stone-600">Mon–Sat: 10:00 AM – 7:00 PM (IST)</p>
                  <p className="mt-1 text-xs text-stone-500">For urgent order issues, calling is usually fastest.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-3xl border border-stone-200/80 bg-white p-5 shadow-md shadow-stone-200/40 ring-1 ring-black/5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500">Quick contact</p>
              <div className="mt-4 space-y-3">
                {vendorEmail && (
                  <a
                    href={`mailto:${vendorEmail}`}
                    className="group flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4 transition hover:border-orange-200 hover:bg-orange-50/60"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-orange-700 ring-1 ring-stone-200 transition group-hover:ring-orange-200">
                      <Mail className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-extrabold text-stone-900">Email</span>
                      <span className="block break-all text-sm font-semibold text-stone-700 group-hover:text-orange-800">{vendorEmail}</span>
                    </span>
                  </a>
                )}
                {vendorPhone && (
                  <a
                    href={`tel:${vendorPhone.replace(/[^+\d]/g, "")}`}
                    className="group flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4 transition hover:border-emerald-200 hover:bg-emerald-50/50"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-700 ring-1 ring-stone-200 transition group-hover:ring-emerald-200">
                      <Phone className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-extrabold text-stone-900">Call</span>
                      <span className="block text-sm font-semibold text-stone-700 group-hover:text-emerald-800">{vendorPhone}</span>
                    </span>
                  </a>
                )}
                {(addressLines.length > 0 || hasMapOpen) && (
                  <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-stone-700 ring-1 ring-stone-200">
                      <MapPin className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-extrabold text-stone-900">Location</span>
                      {addressLines.length > 0 && (
                        <span className="mt-1 block text-sm text-stone-700">
                          {addressLines.map((line, i) => (
                            <span key={i} className="block">{line}</span>
                          ))}
                        </span>
                      )}
                      {hasMapOpen && (
                        <a
                          href={mapOpenUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 underline decoration-orange-300 underline-offset-4 hover:text-orange-900"
                        >
                          Open in Google Maps <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </span>
                  </div>
                )}
                {!vendorEmail && !vendorPhone && addressLines.length === 0 && !hasMapOpen && (
                  <p className="text-sm text-stone-500 px-1">
                    This store hasn't added contact details yet. Check back soon or browse their products.
                  </p>
                )}
              </div>
            </div>
          </div>

          {hasMapEmbed || hasMapOpen ? (
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-3xl border border-stone-200/80 bg-white shadow-xl shadow-stone-200/50 ring-1 ring-black/5">
                <div className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-4 sm:px-6">
                  <p className="text-sm font-extrabold text-stone-900">Find {name}</p>
                  {hasMapOpen && (
                    <a
                      href={mapOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center justify-center rounded-2xl border border-stone-200 bg-white px-3 py-2 text-xs font-bold text-stone-700 shadow-sm transition hover:bg-stone-50"
                    >
                      Open map
                    </a>
                  )}
                </div>
                <div className="h-[18rem] w-full bg-stone-50 sm:h-[22rem]">
                  {hasMapEmbed ? (
                    <iframe
                      title={`${name} location`}
                      src={mapEmbedSrc}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
                      <MapPin className="h-10 w-10 text-orange-400" />
                      <a
                        href={mapOpenUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:from-orange-600 hover:to-amber-600"
                      >
                        Open in Google Maps <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Contact() {
  const { vendorStore, storeSlug } = useVendorStore();

  const email = "saumyashekhar8051@gmail.com";
  const phone = "+91-8804370669";

  const [vendor, setVendor] = useState(null);
  /** Mirrors API `singleVendor` after load (`null` = still loading). */
  const [singleVendorMode, setSingleVendorMode] = useState(null);
  const [loadingVendor, setLoadingVendor] = useState(true);
  /** Admin-configured address/map for multi-vendor Contact page. */
  const [platformContact, setPlatformContact] = useState(null);
  const [loadingPlatform, setLoadingPlatform] = useState(true);

  useEffect(() => {
    let mounted = true;
    const loadStorefront = async () => {
      try {
        const res = await apiClient("GET", "/api/vendors/storefront", null, {
          skipLoading: true,
        });
        if (!mounted) return;
        setSingleVendorMode(res?.singleVendor === true);
        setVendor(res?.vendor ?? null);
      } catch {
        if (mounted) {
          setSingleVendorMode(false);
          setVendor(null);
        }
      } finally {
        if (mounted) setLoadingVendor(false);
      }
    };
    const loadPlatform = async () => {
      try {
        const data = await apiClient("GET", "/api/platform/contact", null, {
          skipLoading: true,
        });
        if (!mounted) return;
        setPlatformContact(data && typeof data === "object" ? data : null);
      } catch {
        if (mounted) setPlatformContact(null);
      } finally {
        if (mounted) setLoadingPlatform(false);
      }
    };
    loadStorefront();
    loadPlatform();
    return () => {
      mounted = false;
    };
  }, []);

  const storeLabel =
    vendor?.storeName?.trim() || vendor?.name?.trim() || "";
  const isSingleStorefront =
    !loadingVendor && singleVendorMode === true && vendor != null;
  const showStoreNameInLocation = isSingleStorefront && !!storeLabel;

  const locationCardTitle = showStoreNameInLocation
    ? `Store location (${storeLabel})`
    : "Store location";

  const pageTitle =
    showStoreNameInLocation ? `Contact ${storeLabel}` : "Contact us";

  const mapPanelTitle = showStoreNameInLocation ? `Find ${storeLabel}` : "Find us";

  const venueForMaps = useMemo(() => {
    if (loadingVendor) return null;
    if (isSingleStorefront) return vendor || null;
    if (loadingPlatform) return null;
    return {
      pickupPointLocation: platformContact?.pickupPointLocation ?? "",
      pickupPointGoogleMapsUrl:
        platformContact?.pickupPointGoogleMapsUrl ?? "",
      pickupPointLat: platformContact?.pickupPointLat ?? null,
      pickupPointLng: platformContact?.pickupPointLng ?? null,
    };
  }, [
    loadingVendor,
    loadingPlatform,
    isSingleStorefront,
    vendor,
    platformContact,
  ]);

  const { embed: mapEmbedSrc, open: mapOpenUrl } = useMemo(
    () => storefrontMapUrls(venueForMaps),
    [venueForMaps],
  );

  const addressLines = splitAddressLines(venueForMaps?.pickupPointLocation);
  const coordLine =
    venueForMaps &&
    typeof venueForMaps.pickupPointLat === "number" &&
    typeof venueForMaps.pickupPointLng === "number"
      ? `Map pin · ${venueForMaps.pickupPointLat}, ${venueForMaps.pickupPointLng}`
      : "";
  const locationLines =
    addressLines.length > 0
      ? addressLines
      : coordLine
        ? [coordLine]
        : [];
  /** External “open in Maps” link (works even when embed URL can’t be built). */
  const hasMapOpen = !!mapOpenUrl;
  const hasMapEmbed = !!mapEmbedSrc;

  const hasPlatformLocationContent =
    !isSingleStorefront &&
    !loadingPlatform &&
    (!!String(platformContact?.pickupPointLocation || "").trim() ||
      !!String(platformContact?.pickupPointGoogleMapsUrl || "").trim() ||
      (typeof platformContact?.pickupPointLat === "number" &&
        typeof platformContact?.pickupPointLng === "number"));

  const mapPanelSubtitle = isSingleStorefront
    ? "Map from storefront vendor pickup point"
    : hasPlatformLocationContent
      ? "Marketplace hub — address & map from admin (multi-vendor)"
      : "Multi-vendor: set address & map in Admin → Marketplace contact";

  const mapsBusy =
    loadingVendor ||
    (!loadingVendor && !isSingleStorefront && loadingPlatform);

  // ─── Vendor-slug mode: show this vendor's contact ───────────────────────────
  if (vendorStore) {
    return <VendorContact vendor={vendorStore} storeSlug={storeSlug} />;
  }

  // ─── Platform / superuser mode (unchanged) ──────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/80 via-amber-50/25 to-stone-100 px-4 py-10 sm:py-14 text-stone-900">
      <Seo
        title="Contact | Bakery in Patna, Bihar"
        description="Contact our Patna bakery for order help, delivery/pickup questions, or custom cake requests."
        canonical="/contact"
      />
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-stone-500">
              Support
            </p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
              {pageTitle}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-stone-600 sm:text-base">
              Need help with an order, payment, delivery/pickup, or your account? Reach us using the
              options below—we typically respond quickly during business hours.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-stone-200/80 bg-white/80 p-4 shadow-sm ring-1 ring-black/5 sm:p-5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/70">
                  <Clock className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-stone-900">Business hours</p>
                  <p className="mt-0.5 text-sm text-stone-600">
                    Mon–Sat: 10:00 AM – 7:00 PM (IST)
                  </p>
                  <p className="mt-1 text-xs text-stone-500">
                    For urgent order issues, calling is usually fastest.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-3xl border border-stone-200/80 bg-white p-5 shadow-md shadow-stone-200/40 ring-1 ring-black/5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500">
                Quick contact
              </p>

              <div className="mt-4 space-y-3">
                <a
                  href={`mailto:${email}`}
                  className="group flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4 transition hover:border-orange-200 hover:bg-orange-50/60"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-orange-700 ring-1 ring-stone-200 transition group-hover:ring-orange-200">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-stone-900">Email</span>
                    <span className="block break-all text-sm font-semibold text-stone-700 group-hover:text-orange-800">
                      {email}
                    </span>
                    <span className="mt-0.5 block text-xs text-stone-500">
                      Best for receipts, issues, and general queries.
                    </span>
                  </span>
                </a>

                <a
                  href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                  className="group flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4 transition hover:border-emerald-200 hover:bg-emerald-50/50"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-emerald-700 ring-1 ring-stone-200 transition group-hover:ring-emerald-200">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-stone-900">Call</span>
                    <span className="block text-sm font-semibold text-stone-700 group-hover:text-emerald-800">
                      {phone}
                    </span>
                    <span className="mt-0.5 block text-xs text-stone-500">
                      Fastest for delivery/pickup and urgent order problems.
                    </span>
                  </span>
                </a>

                <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50/60 p-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-stone-700 ring-1 ring-stone-200">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-stone-900">
                      {locationCardTitle}
                    </span>
                    {mapsBusy ? (
                      <span className="mt-2 block h-14 animate-pulse rounded-xl bg-stone-200/80" />
                    ) : hasMapOpen && locationLines.length > 0 ? (
                      <>
                        <span className="mt-1 block text-sm text-stone-700">
                          {locationLines.map((line, i) => (
                            <span key={`${i}-${line}`} className="block">
                              {line}
                            </span>
                          ))}
                        </span>
                        <a
                          href={mapOpenUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 underline decoration-orange-300 underline-offset-4 hover:text-orange-900"
                        >
                          Open in Google Maps <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </>
                    ) : hasMapOpen ? (
                      <>
                        <p className="mt-1 text-sm text-stone-700">
                          Exact location is pinned on the map — open Maps for directions.
                        </p>
                        <a
                          href={mapOpenUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 underline decoration-orange-300 underline-offset-4 hover:text-orange-900"
                        >
                          Open in Google Maps <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </>
                    ) : isSingleStorefront && vendor ? (
                      <p className="mt-1 text-sm text-stone-600">
                        Store location hasn’t been set on the storefront yet. Use email or phone—
                        we’ll share directions if needed.
                      </p>
                    ) : !isSingleStorefront ? (
                      <p className="mt-1 text-sm text-stone-600">
                        No marketplace address on file yet. Admins can add it under{" "}
                        <span className="font-semibold text-stone-800">
                          Admin → Marketplace contact
                        </span>
                        . You can still reach us by email or phone.
                      </p>
                    ) : (
                      <p className="mt-1 text-sm text-stone-600">
                        Storefront is not available right now. Please use email or phone.
                      </p>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="overflow-hidden rounded-3xl border border-stone-200/80 bg-white shadow-xl shadow-stone-200/50 ring-1 ring-black/5">
              <div className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-stone-900">
                    {mapPanelTitle}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-500 sm:truncate">
                    {mapPanelSubtitle}
                  </p>
                </div>
                {mapOpenUrl ? (
                  <a
                    href={mapOpenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center justify-center rounded-2xl border border-stone-200 bg-white px-3 py-2 text-xs font-bold text-stone-700 shadow-sm transition hover:bg-stone-50"
                  >
                    Open map
                  </a>
                ) : null}
              </div>
              <div className="h-[18rem] w-full bg-stone-50 sm:h-[22rem]">
                {mapsBusy ? (
                  <div className="flex h-full items-center justify-center text-sm font-medium text-stone-500">
                    Loading map…
                  </div>
                ) : hasMapEmbed ? (
                  <iframe
                    title={
                      showStoreNameInLocation
                        ? `${storeLabel} location`
                        : "Store location"
                    }
                    src={mapEmbedSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : mapOpenUrl ? (
                  <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
                    <MapPin className="h-10 w-10 text-orange-400" />
                    <p className="text-sm text-stone-600">
                      Map preview isn’t available for this link. Open Google Maps instead.
                    </p>
                    <a
                      href={mapOpenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:from-orange-600 hover:to-amber-600"
                    >
                      Open in Google Maps <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-stone-600">
                    <MapPin className="h-8 w-8 text-stone-400" />
                    <p>
                      {isSingleStorefront
                        ? "No map location configured for this store yet."
                        : "No marketplace map yet. Configure it in Admin → Marketplace contact."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
